/**
 * Toast notifications adapter.
 *
 * Shape mirrors what the rest of the app already uses (success/error/warning/info)
 * but routes to sonner. Old call sites that imported `useAppStore.addNotification`
 * should migrate to `notify.success` / `notify.error` etc.
 *
 * pt-BR copy is the caller's responsibility — sonner does not own the message text.
 */

import { toast } from 'sonner';

type NotifyOptions = {
  description?: string;
  duration?: number;
  action?: { label: string; onClick: () => void };
};

function buildOptions(opts?: NotifyOptions) {
  if (!opts) return undefined;
  return {
    description: opts.description,
    duration: opts.duration,
    action: opts.action
      ? { label: opts.action.label, onClick: opts.action.onClick }
      : undefined,
  };
}

export const notify = {
  success(message: string, opts?: NotifyOptions) {
    return toast.success(message, buildOptions(opts));
  },
  error(message: string, opts?: NotifyOptions) {
    return toast.error(message, buildOptions(opts));
  },
  warning(message: string, opts?: NotifyOptions) {
    return toast.warning(message, buildOptions(opts));
  },
  info(message: string, opts?: NotifyOptions) {
    return toast.info(message, buildOptions(opts));
  },
  message(message: string, opts?: NotifyOptions) {
    return toast(message, buildOptions(opts));
  },
  loading(message: string) {
    return toast.loading(message);
  },
  dismiss(id?: string | number) {
    return toast.dismiss(id);
  },
  promise: toast.promise,
};
